import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import * as AppEventsAnalyticsActions from '../../../../store/app-events-analytics.actions';

import { ProductPublicModels } from '@features/products/product';
import { SidebarCustomComponents } from '@features/sidebar';
import { OpenSidebarCustom } from '@features/sidebar/infrastructure/state/sidebar.actions';
import { ProductViewNextImageAnalytics, ProductViewZoomInAnalytics } from '@pages/product/store/product-page.actions';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { GA4ImActionType, Ga4ContentType } from '@shared/analytics/GA4/models/event-data.model';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { GA4AnalyticsPublicActions } from '@shared/analytics/GA4/public.api';
import { Ga4Event } from '@shared/analytics/GA4/store/ga4-analytics.actions';

// #region Functions

export const getCustomComponentSelectContentData = (component: SidebarCustomComponents, data: any) => {
    const product: ProductPublicModels.PublicProduct = data?.baseProduct || data?.product;

    return {
        [SidebarCustomComponents.COMING_SOON]: {
            textKey: product?.status?.selectedSize?.name,
            imActionType: product?.status?.selectedSize?.visibilityValue === 'COMING_SOON'
                ? GA4ImActionType.ver_coming_soon : GA4ImActionType.ver_back_soon,
            contentType: Ga4ContentType.product_info,
        },
        [SidebarCustomComponents.OUT_OF_STOCK]: {
            textKey: product?.status?.selectedSize?.name,
            imActionType: GA4ImActionType.ver_sin_stock,
            contentType: Ga4ContentType.product_info,
        },
        /** Dos eventos por orígen, el otro en ga4-analytics-select-content.effects */
        [SidebarCustomComponents.PRODUCT_COMPOSITION_LAYOUT]: {
            textKey: 'dev.product.composition', // REF-AN-8
            imActionType: GA4ImActionType.ver_cuidados,
            contentType: Ga4ContentType.product_info,
        },
        /** Dos eventos por orígen, el otro en ga4-analytics-select-content.effects */
        [SidebarCustomComponents.PRODUCT_SEND_AND_RETURNS]: {
            textKey: 'dev.product.detail.ShippingExchangesReturns', // REF-AN-13
            imActionType: GA4ImActionType.ver_envios,
            contentType: Ga4ContentType.product_info,
        },
    }?.[component];
};

// #endregion

// #region Functional Effects

export const openSidebarSelectContent$ = createEffect((
    actions$ = inject(Actions),
    translateService = inject(TranslateService),
) => actions$.pipe(
    ofType(OpenSidebarCustom),
    concatLatestFrom(({ component, data }) => of(
        getCustomComponentSelectContentData(component, data)
    )),
    filter(([_, value]) => !!value && !!value.textKey),
    concatLatestFrom(([_, { textKey }]) => translateService.get(textKey)),
    map(([[action, eventData], textValue]) => {
        const product = action?.data?.baseProduct || action?.data?.product;
        return GA4AnalyticsPublicActions.Ga4Event({
            name: GA4EventType.select_content,
            eventFrom: EventFrom.productdetail,
            data: {
                contentId: product?.reference,
                contentType: eventData.contentType,
                imActionType: eventData.imActionType,
                items: [product],
                imLinkText: textValue,
            },
        });
    })
), { functional: true });

export const selectContentVerFotoProducto$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(AppEventsAnalyticsActions.VerZoomAnalytics),
    map((action) => Ga4Event({
        name: GA4EventType.select_content,
        eventFrom: EventFrom.productdetail,
        data: {
            contentId: action.product.reference,
            contentType: Ga4ContentType.product_photo,
            imActionType: GA4ImActionType.ver_foto_producto,
            imCarouselIndex: `${action.carouselIndex + 1}/${action.carouselLength}`,
            imLinkType: 'foto',
            imPhotoType: action.typePhoto,
            items: [action.product],
            mediaPath: action.mediaPath,
        },
    }))
), { functional: true });

export const selectContentVerZoomFotoProducto$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(ProductViewNextImageAnalytics, ProductViewZoomInAnalytics),
    map((action) => Ga4Event({
        name: GA4EventType.select_content,
        eventFrom: EventFrom.productdetail,
        data: {
            contentId: action.product.reference,
            contentType: Ga4ContentType.product_photo,
            imActionType: action.type === ProductViewNextImageAnalytics.type ? GA4ImActionType.cambiar_foto
                : GA4ImActionType.ver_zoom,
            imCarouselIndex: `${action.carouselIndex + 1}/${action.carouselLength}`,
            imLinkType: 'foto',
            imPhotoType: action.photoType,
            items: [action.product],
            mediaPath: action.mediaPath,
        },
    }))
), { functional: true });

export const sizeRecommenderOpenEvent$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(OpenSidebarCustom),
    filter(({ component }) => component === SidebarCustomComponents.SASTRE),
    map((action) => {
        return GA4AnalyticsPublicActions.Ga4Event({
            name: GA4EventType.size_recommender_open,
            eventFrom: EventFrom.productdetail,
            data: {
                items: [action.data?.product],
            },
        });
    })
), { functional: true });

export const GA4AnalyticsProductDetailEffects = {
    openSidebarSelectContent$,
    selectContentVerFotoProducto$,
    selectContentVerZoomFotoProducto$,
    sizeRecommenderOpenEvent$,
};

// #endregion
